import Icon from '@atoms/Icon/Icon';
import { IMessage } from '@models/IMessage';
import { IProductListingStatusLabels } from '@models/IProductListingStatusLabels';
import IconSizeEnum from '@models/enums/IconSizeEnum';
import InfoTypeEnum from '@models/enums/InfoTypeEnum';
import ProductStockStatusEnum from '@models/enums/ProductStockStatusEnum';
import InfoModal from '@molecules/InfoModal/InfoModal';
import useAriaLabels from '@utils/hooks/useAriaLabels';
import { useState } from 'react';

interface Props {
  readonly backOrderText: IMessage;
  readonly preOrderText: IMessage;
  readonly status: ProductStockStatusEnum;
  readonly statusLabels: IProductListingStatusLabels;
  readonly addToCartHandler: () => Promise<void>;
  readonly dataTestId?: string;
}

const ProductDetailsStockStatus = ({
  backOrderText,
  preOrderText,
  status,
  statusLabels,
  dataTestId = 'Product-Details-Stock-Status',
  addToCartHandler,
}: Props) => {
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [infoModalContent, setInfoModalContent] = useState<IMessage>({
    title: '',
    text: '',
  });
  const { moreInformationAriaLabel } = useAriaLabels();

  const handleInfoModal = (content: IMessage) => {
    setInfoModalContent(content);
    setIsInfoModalOpen(true);
  };

  const handleModalAddToCartClick = async () => {
    await addToCartHandler();
    setIsInfoModalOpen(false);
  };

  return (
    <div className="body-three mb-5" data-testid={dataTestId}>
      {status === ProductStockStatusEnum.InStock && (
        <span data-testid={`${dataTestId}-In-Stock-Label`}>
          {statusLabels.inStockLabel}
        </span>
      )}
      {status === ProductStockStatusEnum.OutOfStock && (
        <span
          className="text-red-100"
          data-testid={`${dataTestId}-Out-Of-Stock-Label`}
        >
          {statusLabels.outOfStockLabel}
        </span>
      )}
      {status === ProductStockStatusEnum.ComingSoon && (
        <span
          className="text-green-100"
          data-testid={`${dataTestId}-Coming-Soon-Label`}
        >
          {statusLabels.comingSoonLabel}
        </span>
      )}
      {status === ProductStockStatusEnum.PreOrder && (
        <div className="flex items-center space-x-2 text-green-100">
          <span data-testid={`${dataTestId}-Preorder-Label`}>
            {preOrderText?.title}
          </span>

          <button
            type="button"
            className="flex -translate-y-2"
            onClick={() => {
              handleInfoModal(preOrderText);
            }}
            aria-label={moreInformationAriaLabel}
            data-testid={`${dataTestId}-Preorder-Button`}
          >
            <Icon
              name="info"
              colorClassName="text-green-100"
              size={IconSizeEnum.md}
            />
          </button>
        </div>
      )}
      {status === ProductStockStatusEnum.BackOrder && (
        <div className="flex items-center space-x-2 text-green-100">
          <span data-testid={`${dataTestId}-Back-Order-Label`}>
            {backOrderText?.title}
          </span>
          <button
            type="button"
            className="flex -translate-y-2"
            onClick={() => {
              handleInfoModal(backOrderText);
            }}
            aria-label={moreInformationAriaLabel}
            data-testid={`${dataTestId}-Back-Order-Button`}
          >
            <Icon
              name="info"
              colorClassName="text-green-100"
              size={IconSizeEnum.md}
            />
          </button>
        </div>
      )}
      {status === ProductStockStatusEnum.SoldOut && (
        <span
          className="text-red-100"
          data-testid={`${dataTestId}-Sold-Out-Label`}
        >
          {statusLabels.soldOutLabel}
        </span>
      )}

      <InfoModal
        type={InfoTypeEnum.Information}
        isModalOpen={isInfoModalOpen}
        toggleModal={setIsInfoModalOpen}
        title={infoModalContent.title}
        description={infoModalContent.text}
        buttonText={statusLabels.addToCartCTA}
        handleCTAClick={handleModalAddToCartClick}
        dataTestId={`${dataTestId}-Info-Modal`}
      />
    </div>
  );
};

export default ProductDetailsStockStatus;
